import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type { InspectionLine } from '@obraplan/core/application/use-cases/planning/inspection-use-cases';
import type { DailyReportTask } from '@obraplan/core/domain/planning/daily-report-task';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import { membershipOpenOn } from '@obraplan/core/domain/workforce/team';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';
import {
  DailyReportWork,
  type ReportTeamWorkInput,
  type ReportWorkInput,
  type TrocaDeAtividade,
  type WorkResourceOption,
  type WorkTeamOption,
} from './DailyReportWork';
import type { CarregarPecas } from './PecasDoDia';
import type { CarregarComodos } from './ComodosDoDia';
import {
  equipeNaFila,
  RDO_NA_FILA,
  type ConferenceOperation,
  type OperationOutcome,
} from '@/app/outbox-types';
import { AvisoDaFila } from './AvisoDaFila';
import { GUARDADO_NO_APARELHO, syncDoCartao } from './fila-da-conferencia';
import { jornadasComAFila } from './horas-da-fila';
import { chaveDaFrente, frentesDoDia } from './frentes-do-dia';
import { useFilaPorCima } from './use-fila-por-cima';
import { frentePelaDisciplina, frenteSemFicha, lotesPorDisciplina } from './frente-pela-disciplina';
import { mensagemDasFotos, type FotosDasNcs } from './subir-fotos-das-ncs';
import { NcPlacesContext } from './nc-places';
import { useNcPlacesTools } from './use-nc-places-tools';

/**
 * A seção "Mão de obra do dia" ligada ao container (Fase 20).
 *
 * Duas leituras — a qualidade do dia e a mão de obra da obra — e duas escritas: lançar
 * trabalho (alocação + conferência + medição + checklist, numa transação) e gravar a
 * avulsa na lista inteira das avulsas. A qualidade é relida com a frente escolhida no
 * select, para que a checklist dela chegue antes do modal abrir. Os cartões da conferência
 * vêm prontos da página, que é quem tem os diálogos deles.
 */
export function DailyReportWorkSection({
  projectId,
  date,
  editable,
  tasks,
  conference,
  lines,
  troca,
  onEncerrarTroca,
}: {
  projectId: ProjectId;
  date: string;
  editable: boolean;
  tasks: readonly DailyReportTask[];
  conference: ReactNode;
  /** As linhas do dia como o servidor as leu: o ajuste e o "Tirar" da fila mudam as horas delas. */
  lines?: readonly InspectionLine[];
  /** A atividade errada que o "Trocar" do dia da pessoa mandou corrigir (RN-645). */
  troca?: TrocaDeAtividade | undefined;
  onEncerrarTroca?: (() => void) | undefined;
}) {
  const container = useContainer();
  const { session } = useSessionState();
  const [frenteEscolhida, setFrenteEscolhida] = useState<string | undefined>(undefined);
  const ncPlaces = useNcPlacesTools(projectId, date);

  const pronto = session !== undefined;

  // A qualidade do dia, sem frente escolhida: a mesma leitura da Conferência e da pré-carga, que
  // por isso está no aparelho sem sinal. Dela saem as torres, os pavimentos e as atividades do
  // lançamento — e escolher uma não as apaga mais (RN-618).
  const quality = useLive(
    () =>
      pronto
        ? container.getDayQuality.execute({ projectId, date }, session)
        : Promise.resolve(undefined),
    [session?.userId, projectId, date],
  );
  // A frente escolhida, inteira: as fichas e o que já foi verificado hoje, antes do modal abrir.
  const escolhida = useLive(
    () =>
      pronto && frenteEscolhida !== undefined
        ? container.getDayQuality.execute(
            { projectId, date, activityIds: [frenteEscolhida] },
            session,
          )
        : Promise.resolve(undefined),
    [session?.userId, projectId, date, frenteEscolhida],
  );
  // Sem ela (sem sinal, e ninguém a abriu antes), as fichas vêm de uma frente da mesma
  // disciplina, lida pela pré-carga (`frente-pela-disciplina.ts`).
  const lotes =
    quality !== undefined && quality.ok ? lotesPorDisciplina(quality.value.projectFronts) : [];
  const semAEscolhida = escolhida !== undefined && !escolhida.ok;
  const porDisciplina = useLive(async () => {
    if (!pronto || !semAEscolhida) return [];
    const lidas = await Promise.all(
      lotes.map((lote) =>
        container.getDayQuality.execute({ projectId, date, activityIds: lote }, session),
      ),
    );
    return lidas.flatMap((lida) => (lida.ok ? lida.value.fronts : []));
  }, [
    session?.userId,
    projectId,
    date,
    semAEscolhida,
    lotes.map((lote) => lote.join(',')).join(';'),
  ]);

  // A fila do aparelho neste dia: o lançamento feito sem sinal espera nela (RN-617).
  const fila = useLive(
    () =>
      pronto ? container.outbox.list(session.userId, { projectId, date }) : Promise.resolve([]),
    [session?.userId, projectId, date],
  );
  const syncDosLancamentos = syncDoCartao(
    (fila ?? []).filter(
      (entry) =>
        entry.operation.kind === 'lancar-trabalho' ||
        entry.operation.kind === 'lancar-trabalho-equipe',
    ),
  );
  // A hora extra conta o que a fila ainda vai gravar (RN-641). O que já subiu continua na conta
  // até a qualidade do dia ser relida, para não sumir no intervalo entre as duas.
  const entradasDoDia = useFilaPorCima(fila, quality);
  const jornadas = useMemo(
    () =>
      quality !== undefined && quality.ok
        ? jornadasComAFila(quality.value.workdays, entradasDoDia, lines)
        : undefined,
    [quality, entradasDoDia, lines],
  );
  // A frente × serviço que a pessoa já tem no dia, no servidor ou na fila (RN-643).
  const frentes = useMemo(() => frentesDoDia(entradasDoDia, lines), [entradasDoDia, lines]);
  const hasFront = useCallback(
    (resourceId: string, activityId: string, serviceId: string | undefined) =>
      frentes.has(chaveDaFrente(resourceId, activityId, serviceId)),
    [frentes],
  );
  /** Grava agora, ou guarda no aparelho quando não há sinal (RN-603). */
  const submeter = useCallback(
    (operacao: ConferenceOperation) => container.outbox.submit(operacao, session!),
    [container, session],
  );
  const workforce = useLive(
    () =>
      pronto ? container.getWorkforce.execute(projectId, session) : Promise.resolve(undefined),
    [session?.userId, projectId],
  );

  // O modo de medição da obra (Fase 30): na obra por equipe, o lançamento ganha "Por equipe".
  const project = useLive(
    () => (pronto ? container.getProject.execute(projectId, session) : Promise.resolve(undefined)),
    [session?.userId, projectId],
  );
  const porEquipe =
    project !== undefined && project.ok && project.value.measurementMode === 'POR_EQUIPE';

  const carregarPecas = useCallback<CarregarPecas>(
    async ({ activityId, serviceId, date: dia }) => {
      if (session === undefined) return undefined;
      const lido = await container.getFrontPieces.execute(
        { projectId, activityId, serviceId, date: dia },
        session,
      );
      return lido.ok ? lido.value : undefined;
    },
    [container, session, projectId],
  );

  const carregarComodos = useCallback<CarregarComodos>(
    async ({ activityId, serviceId, date: dia }) => {
      if (session === undefined) return undefined;
      const lido = await container.getFrontRooms.execute(
        { projectId, activityId, serviceId, date: dia },
        session,
      );
      return lido.ok ? lido.value : undefined;
    },
    [container, session, projectId],
  );

  /*
   * Lançar o que ninguém planejou (RN-617): os cômodos, as peças, o trabalho com a checklist e
   * as fotos das NCs, na sequência de sempre (`conference-operations.ts`). Com sinal sobe na
   * hora; sem sinal fica no aparelho e sobe quando ele voltar, como o resto da Conferência.
   */
  const [lancar, lancarState] = useCommand(
    (input: ReportWorkInput) =>
      submeter({
        kind: 'lancar-trabalho',
        projectId,
        date,
        resourceId: input.resourceId,
        input,
      }),
    {
      successMessage: (resultado) =>
        resultado.queued
          ? GUARDADO_NO_APARELHO
          : [
              resultado.outcome.nonConformities > 0
                ? `Trabalho lançado · ${String(resultado.outcome.nonConformities)} não conformidade(s) aberta(s)`
                : 'Trabalho lançado e conferido',
              ...mensagemDasFotos(fotosDe(resultado.outcome)),
            ].join(' · ') + '.',
    },
  );
  // A troca da atividade errada (RN-645) é o mesmo lançamento, com a errada junto; só a mensagem
  // muda, e por isso o comando é outro.
  const [trocar, trocarState] = useCommand(
    (input: ReportWorkInput) =>
      submeter({
        kind: 'lancar-trabalho',
        projectId,
        date,
        resourceId: input.resourceId,
        input,
      }),
    {
      successMessage: (resultado) =>
        resultado.queued
          ? GUARDADO_NO_APARELHO
          : [
              resultado.outcome.nonConformities > 0
                ? `Atividade trocada · ${String(resultado.outcome.nonConformities)} não conformidade(s) aberta(s)`
                : 'Atividade trocada',
              ...mensagemDasFotos(fotosDe(resultado.outcome)),
            ].join(' · ') + '.',
    },
  );
  // O lançamento da equipe (Fase 30, etapa 2 — RN-421): as peças antes, como no da pessoa, e
  // depois as horas de cada um e a quantidade da equipe numa transação só, no servidor.
  const [lancarEquipe, lancarEquipeState] = useCommand(
    (input: ReportTeamWorkInput) =>
      submeter({
        kind: 'lancar-trabalho-equipe',
        projectId,
        date,
        resourceId: equipeNaFila(input.teamId, input.activityId, input.serviceId),
        input,
      }),
    {
      successMessage: (resultado) =>
        resultado.queued
          ? GUARDADO_NO_APARELHO
          : [
              resultado.outcome.nonConformities > 0
                ? `Trabalho da equipe lançado · ${String(resultado.outcome.nonConformities)} não conformidade(s) aberta(s)`
                : 'Trabalho da equipe lançado e conferido',
              ...mensagemDasFotos(fotosDe(resultado.outcome)),
            ].join(' · ') + '.',
    },
  );
  // A avulsa vai na lista inteira do RDO, pela mesma fila das tarefas do documento (RN-612).
  const [gravarAvulsas, avulsasState] = useCommand(
    (lista: readonly DailyReportTask[]) =>
      submeter({ kind: 'rdo-tarefas', projectId, date, resourceId: RDO_NA_FILA, tasks: lista }),
    {
      successMessage: (resultado) =>
        resultado.queued ? GUARDADO_NO_APARELHO : 'Tarefa avulsa lançada.',
    },
  );

  const resources = useMemo(
    () =>
      workforce === undefined || !workforce.ok
        ? []
        : workforce.value.resources
            .filter((item) => item.resource.isActive && item.resource.deletedAt === undefined)
            .map((item) => ({
              resourceId: item.resource.id,
              name: item.name,
              detail: item.detail,
            }))
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [workforce],
  );

  /*
   * As equipes do dia, com quem está nelas **naquela data** (RN-053): lançar a semana passada
   * com a composição desta semana poria na equipe quem ainda não estava. Equipe sem ninguém no
   * dia não aparece.
   */
  const teams = useMemo((): readonly WorkTeamOption[] | undefined => {
    if (!porEquipe || workforce === undefined || !workforce.ok) return undefined;
    const view = workforce.value;
    const porFuncionario = new Map<string, WorkResourceOption>();
    for (const item of view.resources) {
      const employeeId = item.resource.employeeId;
      if (employeeId === undefined || !item.resource.isActive) continue;
      if (item.resource.deletedAt !== undefined) continue;
      porFuncionario.set(employeeId, {
        resourceId: item.resource.id,
        name: item.name,
        detail: item.detail,
      });
    }
    return view.teams
      .filter((team) => team.isActive && team.deletedAt === undefined)
      .map((team) => ({
        teamId: team.id,
        name: team.name,
        members: view.members
          .filter((member) => member.teamId === team.id && membershipOpenOn(member, date))
          .flatMap((member) => {
            const opcao = porFuncionario.get(member.employeeId);
            return opcao === undefined ? [] : [opcao];
          })
          .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
      }))
      .filter((team) => team.members.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [porEquipe, workforce, date]);

  const fronts = quality !== undefined && quality.ok ? quality.value.projectFronts : [];
  const serviceCatalog = quality !== undefined && quality.ok ? quality.value.serviceCatalog : [];
  const nonConformities = quality !== undefined && quality.ok ? quality.value.nonConformities : [];
  const erro =
    lancarState.error ?? trocarState.error ?? lancarEquipeState.error ?? avulsasState.error;

  return (
    // O passo de qualidade do lançamento lê daqui os lugares do dia e o encerrar (Fase 37).
    <NcPlacesContext.Provider value={ncPlaces}>
      <DailyReportWork
        conference={
          <>
            {conference}
            {syncDosLancamentos !== undefined && (
              <AvisoDaFila
                sync={syncDosLancamentos}
                pendente={
                  syncDosLancamentos.count === 1
                    ? '1 lançamento aguardando envio'
                    : `${String(syncDosLancamentos.count)} lançamentos aguardando envio`
                }
                recusado="O servidor não aceitou o lançamento feito sem sinal"
              />
            )}
          </>
        }
        resources={resources}
        {...(jornadas === undefined ? {} : { workdays: jornadas })}
        hasFront={hasFront}
        fronts={fronts}
        serviceCatalog={serviceCatalog}
        frontDetails={(activityId) => {
          const lidas = [escolhida, quality].flatMap((lida) =>
            lida !== undefined && lida.ok ? lida.value.fronts : [],
          );
          const lida = lidas.find((front) => front.activityId === activityId);
          if (lida !== undefined || !semAEscolhida) return lida;
          return (
            frentePelaDisciplina(activityId, fronts, [...(porDisciplina ?? []), ...lidas]) ??
            (porDisciplina === undefined
              ? undefined
              : frenteSemFicha(activityId, fronts, serviceCatalog))
          );
        }}
        nonConformities={nonConformities}
        {...(quality !== undefined && quality.ok
          ? { pendingNonConformities: quality.value.pendingNonConformities }
          : {})}
        {...(quality !== undefined && quality.ok
          ? { nonConformityOwners: quality.value.nonConformityOwners }
          : {})}
        tasks={tasks}
        editable={editable}
        isPending={
          lancarState.isPending ||
          trocarState.isPending ||
          lancarEquipeState.isPending ||
          avulsasState.isPending
        }
        errorMessage={erro?.message}
        onSelectFront={setFrenteEscolhida}
        pieces={{ date, load: carregarPecas }}
        rooms={{ date, load: carregarComodos }}
        onReport={async (input) => {
          const resultado = await (input.replaces === undefined ? lancar(input) : trocar(input));
          return resultado !== undefined && resultado.ok;
        }}
        troca={troca}
        onEncerrarTroca={onEncerrarTroca}
        teams={teams}
        onReportTeam={async (input) => {
          const resultado = await lancarEquipe(input);
          return resultado !== undefined && resultado.ok;
        }}
        onAddTask={async (lista) => {
          const resultado = await gravarAvulsas(lista);
          return resultado !== undefined && resultado.ok;
        }}
      />
    </NcPlacesContext.Provider>
  );
}

/** As fotos das NCs como a fila as conta, na forma da mensagem do lançamento. */
function fotosDe(outcome: OperationOutcome): FotosDasNcs {
  return { enviadas: outcome.photos.sent, falhas: outcome.photos.failures };
}
